import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, TrendingUp, Users, Building2,
  ArrowRight, Award, ChevronRight,
  Heart, Coins, Briefcase
} from 'lucide-react';
import Calculator from '../components/Calculator';

export default function Home() {
  const [activeTab, setActiveTab] = useState('deposits');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [digitalTab, setDigitalTab] = useState('benefits');
  const [expandedWay, setExpandedWay] = useState('online');

  const slides = [
    { image: '/Banner 1.png', alt: 'Sahyadri Urban Banner 1' },
    { image: '/Banner 2.png', alt: 'Sahyadri Urban Banner 2' }
  ];

  // Auto transition banner slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 2);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    { value: '18,500+', label: 'Active Members', icon: Users },
    { value: '₹342 Cr', label: 'Deposits Portfolio', icon: Coins },
    { value: '₹220 Cr', label: 'Loans Disbursed', icon: TrendingUp },
    { value: '12+', label: 'Branches & ATMs', icon: Building2 },
    { value: '20+ Yrs', label: 'Years of Trust', icon: Award },
    { value: '99.9%', label: 'Uptime Digital Txns', icon: Shield }
  ];

  const categories = {
    deposits: [
      { name: 'Fixed Deposit (FD)', rate: 'Up to 8.75%', desc: 'Earn maximum interest with compound growth and flexible tenures.' },
      { name: 'Savings Account', rate: '4.00% p.a.', desc: 'High flexibility with secure transactions and instant UPI transfers.' },
      { name: 'Recurring Deposit (RD)', rate: '7.25% p.a.', desc: 'Save monthly, harvest hefty returns at maturity. Flexible terms.' },
      { name: 'Senior Citizen Deposit', rate: '8.75% p.a.', desc: 'Extra 0.50% yield with monthly or quarterly payout options.' }
    ],
    loans: [
      { name: 'Agriculture Gold Loan', rate: 'From 9.25%', desc: 'Quick funds for farmers against gold ornaments. Under 30 mins.' },
      { name: 'Personal Loan', rate: 'From 10.50%', desc: 'Easy personal financing with zero hidden costs and minimal document checks.' },
      { name: 'Home Loan', rate: 'From 8.90%', desc: 'Build your dream house with flexible EMIs and long repayments.' },
      { name: 'MSME Business Loan', rate: 'From 9.75%', desc: 'Empower local trade and store setups with capital funding.' }
    ]
  };

  const digitalTabData = {
    benefits: {
      title: 'Cooperative Membership Benefits',
      indicatorsLeft: ['High Dividend Payouts', 'Subsidized Agri Loans'],
      indicatorsRight: ['Democratic Management', 'Community Upliftment'],
      phone1Title: 'Member Desk',
      phone1Balance: 'Member Ledger',
      phone2Title: 'Core Banking',
      desc: 'Gain special voting rights, annual dividend yields, and customized credit lines designed to boost local trade and micro farming.'
    },
    explore: {
      title: 'Extensive Branch Network & Audits',
      indicatorsLeft: ['12+ Branches Active', 'Government Audited'],
      indicatorsRight: ['Secure Safety Vaults', 'Instant Ledger Registry'],
      phone1Title: 'Ledger Desk',
      phone1Balance: 'Audit Complete',
      phone2Title: 'Annual Audits',
      desc: 'Visit any of our branches across Sangli and rural regions. Fully compliant with state-level auditing, keeping your funds safe.'
    },
    metaverse: {
      title: 'Subsidized Agriculture & Crop Loans',
      indicatorsLeft: ['Subsidized Crop Rates', 'Tractor Finance Options'],
      indicatorsRight: ['No Hidden Charges', 'Harvest-Linked EMIs'],
      phone1Title: 'Agro Credit',
      phone1Balance: 'Starts 7.50% p.a.',
      phone2Title: 'Agro Desk',
      desc: 'Custom credit facilities for purchasing seeds, fertilizers, and modern drip irrigation machinery with specialized low interest rates for farmers.'
    },
    whatsapp: {
      title: 'Dedicated Women Savings Schemes',
      indicatorsLeft: ['Zero Balance Ledgers', 'Special Savings Rates'],
      indicatorsRight: ['Micro Finance Lines', 'Community Seminars'],
      phone1Title: 'Empowerment',
      phone1Balance: '4.25% p.a. Savings',
      phone2Title: 'Self-Help Groups',
      desc: 'Promoting financial independence with higher savings returns, zero maintenance fees, and tailored micro-finance credit for self-help groups.'
    },
    netbanking: {
      title: 'Special Student & Youth Savings',
      indicatorsLeft: ['Zero Minimum Balances', 'Free Exam Fee Drafts'],
      indicatorsRight: ['Digital Savings Access', 'Financial Literacy Camps'],
      phone1Title: 'Youth Savings',
      phone1Balance: 'Student Ledger',
      phone2Title: 'E-Education Check',
      desc: 'Start secure savings habits early. We offer zero-minimum balance student ledgers and free processing for academic drafts.'
    }
  };

  const waysToBankData = [
    {
      id: 'online',
      title: 'Online / Mobile Banking',
      desc: 'Access your account anywhere, anytime. Pay bills, transfer funds via NEFT/RTGS, open new deposits, and check statements inside our secure member portal.',
      link: '/login'
    },
    {
      id: 'upi',
      title: 'UPI Payment Services',
      desc: 'Link your Sahyadri savings account to popular UPI apps like BHIM, Google Pay, or PhonePe. Enjoy instant peer-to-peer transfers with zero hidden transaction costs.',
      link: '/services#transfers'
    },
    {
      id: 'branch',
      title: 'Personalized Branch Banking',
      desc: 'Visit any of our 12+ modern branches across Maharashtra. Experience warm cooperative service combined with professional, digitized cash and audit desks.',
      link: '/contact'
    },
    {
      id: 'atm',
      title: 'Secure ATM Network',
      desc: 'Withdraw cash or query balances 24/7 using our extensive network of shared ATMs. Fortified with biometric check limits and high-grade safety cameras.',
      link: '/contact'
    },
    {
      id: 'phone',
      title: 'Toll-Free Phone Banking',
      desc: 'Call our dedicated service line to talk with certified bank representatives, process query tickets, verify balances, or trigger emergency debit freezes.',
      link: '/contact'
    },
    {
      id: 'sms',
      title: 'SMS Alerts & Notice Logs',
      desc: 'Receive immediate SMS alerts on all debit/credit transactions, scheduled interest credits, maturity dates, and security login warnings.',
      link: '/faq'
    },
    {
      id: 'missedcall',
      title: 'Missed Call Balance Check',
      desc: 'Give a missed call from your registered mobile number to receive your latest ledger balance and account status summary via instant SMS.',
      link: '/faq'
    }
  ];

  return (
    <div className="overflow-hidden bg-[#F7F9FC]">
      {/* 1. HERO CAROUSEL SECTION */}
      <div className="bg-[#052046] pt-20">
        <section className="relative w-full h-[180px] sm:h-[280px] md:h-[360px] lg:h-[450px] xl:h-[540px] overflow-hidden bg-white">
          {/* Slides */}
          <div className="relative w-full h-full">
            {slides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {/* Carousel Indicators (Dots) */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex space-x-2.5 z-20">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-8 bg-[#B31825]' : 'w-2.5 bg-white/50 hover:bg-white'
                  }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </section>
      </div>

      {/* 2. PREMIUM ANNOUNCEMENT MARQUEE SECTION */}
      <section className="bg-[#052046] border-y border-white/5 py-3 overflow-hidden z-20 relative">
        <div className="w-full px-4 sm:px-8 lg:px-12 flex items-center">
          {/* Tag / Title */}
          <div className="bg-[#B31825] text-white text-[11px] sm:text-xs font-montserrat font-bold uppercase tracking-wider px-3 py-1 rounded-md shrink-0 shadow-md z-30 mr-4 flex items-center space-x-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span>Alerts & Updates</span>
          </div>

          {/* Marquee Content */}
          <div className="relative w-full overflow-hidden flex items-center text-xs sm:text-sm text-gray-200 font-poppins">
            <div className="animate-marquee whitespace-nowrap flex space-x-12">
              <span>🔒 <strong>Security Warning:</strong> Dial <strong>1930</strong> for online Financial Fraud or report immediately on cybercrime.gov.in.</span>
              <span>📈 <strong>Special FD Rates:</strong> High yield fixed deposit rates up to <strong>8.75% p.a.</strong> for Senior Citizens.</span>
              <span>📞 <strong>Member Helpdesk:</strong> Contact our toll-free customer support hotline at <strong>1800-270-0222</strong>.</span>
              <span>🚫 <strong>Safe Banking:</strong> Never share your Member Portal login passcode, credit details, or SMS OTP with anyone.</span>

              {/* Duplicate for infinite loop */}
              <span>🔒 <strong>Security Warning:</strong> Dial <strong>1930</strong> for online Financial Fraud or report immediately on cybercrime.gov.in.</span>
              <span>📈 <strong>Special FD Rates:</strong> High yield fixed deposit rates up to <strong>8.75% p.a.</strong> for Senior Citizens.</span>
              <span>📞 <strong>Member Helpdesk:</strong> Contact our toll-free customer support hotline at <strong>1800-270-0222</strong>.</span>
              <span>🚫 <strong>Safe Banking:</strong> Never share your Member Portal login passcode, credit details, or SMS OTP with anyone.</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT SOCIETY PREVIEW */}
      <section className="py-24 bg-white text-navy font-sans relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left Visual */}
          <div className="lg:col-span-6 relative">
            <div className="bg-gradient-to-tr from-primary to-primary-light rounded-3xl p-3 shadow-2xl relative overflow-hidden">
              <div className="bg-white rounded-2xl overflow-hidden aspect-video flex items-center justify-center text-primary-dark">
                {/* SVG Illustration - Professional Branch Front */}
                <svg className="w-full h-full bg-slate-50" viewBox="0 0 800 450" fill="none">
                  <rect width="800" height="450" fill="#F8FAFC" />
                  <path d="M100 400 H700 V430 H100 Z" fill="#E2E8F0" />
                  <rect x="150" y="150" width="500" height="250" fill="#FFFFFF" stroke="#052046" strokeWidth="8" rx="8" />
                  <rect x="180" y="200" width="80" height="200" fill="#F1F5F9" />
                  <rect x="540" y="200" width="80" height="200" fill="#F1F5F9" />
                  <rect x="310" y="240" width="180" height="160" fill="#052046" rx="4" />
                  <line x1="400" y1="240" x2="400" y2="400" stroke="#FFFFFF" strokeWidth="4" />
                  <polygon points="120,150 400,30 680,150" fill="#020C1A" />
                  <circle cx="400" cy="90" r="25" fill="#B31825" />
                  <path d="M390 100 L400 80 L410 100 Z" fill="#020C1A" />
                  <text x="400" y="180" textAnchor="middle" fill="#020C1A" fontSize="24" fontWeight="bold" fontFamily="Montserrat">SAHYADRI BHAVAN</text>
                  <text x="400" y="205" textAnchor="middle" fill="#B31825" fontSize="14" fontWeight="600" letterSpacing="2" fontFamily="Montserrat">CO-OPERATIVE CREDIT SOCIETY</text>
                </svg>
              </div>
            </div>
            {/* Overlay statistics badge */}
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-[#B31825] to-[#8a0f19] text-white p-6 rounded-2xl shadow-xl border border-[#B31825] max-w-xs hidden sm:block">
              <h4 className="font-montserrat font-bold text-2xl">20+ Years</h4>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/90 mt-1">Of Unbroken Financial Excellence & Community Upliftment</p>
            </div>
          </div>

          {/* Right Text */}
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block">About Our Institution</span>
            <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy tracking-tight leading-tight">
              Serving the Community, <br />
              Powering Economic Inclusion
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Sahyadri Urban Co-operative Credit Society Ltd. was founded with a singular vision: to bring banking stability and affordable credit directly to the grassroots. From retail depositors to small-scale retailers, farmers, and self-help groups, we provide security, transparency, and attractive yields.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-4">
              <div className="flex items-start space-x-3 bg-neutralBg p-3 rounded-xl border border-gray-100">
                <span className="text-emerald text-base font-bold select-none mt-0.5">✓</span>
                <div>
                  <h4 className="font-bold text-sm text-navy">High-Yield Interest</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Premium returns up to 8.75% on FD schemes</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-neutralBg p-3 rounded-xl border border-gray-100">
                <span className="text-emerald text-base font-bold select-none mt-0.5">✓</span>
                <div>
                  <h4 className="font-bold text-sm text-navy">Flexible Agri Credit</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Fast, low-document loans for rural farmers</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-neutralBg p-3 rounded-xl border border-gray-100">
                <span className="text-emerald text-base font-bold select-none mt-0.5">✓</span>
                <div>
                  <h4 className="font-bold text-sm text-navy">Online Portal Setup</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Safe member portal access with OTP security checks</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-neutralBg p-3 rounded-xl border border-gray-100">
                <span className="text-emerald text-base font-bold select-none mt-0.5">✓</span>
                <div>
                  <h4 className="font-bold text-sm text-navy">Democratic Heritage</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Owned by members, driving local community progress</p>
                </div>
              </div>
            </div>

            <Link
              to="/about"
              className="inline-flex items-center space-x-2 text-primary font-bold hover:text-primary-dark transition-colors border-b-2 border-primary hover:border-primary-dark pb-0.5"
            >
              <span>Know More About Our History</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 3. BANKING STATISTICS - COUNTER CARDS */}
      <section className="bg-navy-dark py-20 px-4 sm:px-6 lg:px-8 text-white relative border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-montserrat font-bold text-3xl">Sahyadri in Numbers</h2>
            <p className="text-gray-400 text-sm mt-2">Driven by members, certified by trust, leading with digital excellence.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="bg-navy border border-white/10 rounded-2xl p-6 text-center flex flex-col justify-between space-y-4 hover:-translate-y-2 transition-all duration-300 shadow-lg group">
                  <div className="mx-auto p-3.5 bg-primary/20 text-[#B31825] rounded-xl w-fit group-hover:bg-[#B31825] group-hover:text-white transition-all">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="font-montserrat font-extrabold text-2xl sm:text-3xl text-white block">{stat.value}</span>
                    <span className="text-[10px] text-gray-400 font-bold tracking-widest uppercase block mt-1.5">{stat.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. DIGITAL BANKING SERVICES SECTION (Reference Layout input_file_2.png) */}
      <section className="py-24 bg-neutralBg text-navy border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Cooperative Services</span>
            <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Secure. Community-Focused. Trusted.</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Explore our member programs, specialized credit schemes, and audited safety slabs. Click tabs to preview.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Tabs (Capsules) */}
            <div className="lg:col-span-4 flex flex-col space-y-3.5">
              <button
                onClick={() => setDigitalTab('benefits')}
                className={`w-full py-4 px-6 text-left rounded-full font-montserrat font-bold text-sm transition-all duration-300 border shadow-sm ${digitalTab === 'benefits'
                  ? 'bg-[#B31825] text-white border-[#B31825] scale-102 font-extrabold'
                  : 'bg-white text-navy border-gray-200 hover:bg-gray-50'
                  }`}
              >
                Membership Benefits
              </button>
              <button
                onClick={() => setDigitalTab('explore')}
                className={`w-full py-4 px-6 text-left rounded-full font-montserrat font-bold text-sm transition-all duration-300 border shadow-sm ${digitalTab === 'explore'
                  ? 'bg-[#B31825] text-white border-[#B31825] scale-102 font-extrabold'
                  : 'bg-white text-navy border-gray-200 hover:bg-gray-50'
                  }`}
              >
                Branch Network & Audits
              </button>
              <button
                onClick={() => setDigitalTab('metaverse')}
                className={`w-full py-4 px-6 text-left rounded-full font-montserrat font-bold text-sm transition-all duration-300 border shadow-sm ${digitalTab === 'metaverse'
                  ? 'bg-[#B31825] text-white border-[#B31825] scale-102 font-extrabold'
                  : 'bg-white text-navy border-gray-200 hover:bg-gray-50'
                  }`}
              >
                Agriculture & Crop Support
              </button>
              <button
                onClick={() => setDigitalTab('whatsapp')}
                className={`w-full py-4 px-6 text-left rounded-full font-montserrat font-bold text-sm transition-all duration-300 border shadow-sm ${digitalTab === 'whatsapp'
                  ? 'bg-[#B31825] text-white border-[#B31825] scale-102 font-extrabold'
                  : 'bg-white text-navy border-gray-200 hover:bg-gray-50'
                  }`}
              >
                Women Empowerment Slabs
              </button>
              <button
                onClick={() => setDigitalTab('netbanking')}
                className={`w-full py-4 px-6 text-left rounded-full font-montserrat font-bold text-sm transition-all duration-300 border shadow-sm ${digitalTab === 'netbanking'
                  ? 'bg-[#B31825] text-white border-[#B31825] scale-102 font-extrabold'
                  : 'bg-white text-navy border-gray-200 hover:bg-gray-50'
                  }`}
              >
                Youth & Student Accounts
              </button>
            </div>

            {/* Right Interactive Mockups & Flow Indicators */}
            <div className="lg:col-span-8 bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-inner grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative overflow-hidden min-h-[420px]">
              {/* Info text */}
              <div className="md:col-span-4 space-y-4">
                <h3 className="font-montserrat font-extrabold text-xl text-[#052046]">{digitalTabData[digitalTab].title}</h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{digitalTabData[digitalTab].desc}</p>
                <Link to="/services" className="inline-flex items-center space-x-1 text-xs font-bold text-[#B31825] uppercase tracking-wider hover:underline pt-2">
                  <span>Explore Our Services</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {/* Dynamic Mockup Frames & Arrows */}
              <div className="md:col-span-8 flex items-center justify-around relative px-4 select-none">
                {/* Pointer Badge Left */}
                <div className="flex flex-col space-y-4 z-10">
                  {digitalTabData[digitalTab].indicatorsLeft.map((ind, idx) => (
                    <div key={idx} className="bg-neutralBg border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm text-[11px] font-bold text-navy whitespace-nowrap animate-pulse">
                      {ind}
                    </div>
                  ))}
                </div>

                {/* Smartphone Mockup 1 */}
                <div className="w-[125px] h-[240px] bg-slate-900 rounded-[28px] border-[5px] border-slate-800 shadow-2xl relative flex flex-col items-center justify-between p-2 overflow-hidden mx-2 shrink-0">
                  <div className="w-[60px] h-[10px] bg-slate-800 rounded-full absolute top-1" />
                  <div className="w-full flex-grow flex flex-col justify-between pt-4 bg-gradient-to-b from-[#052046] to-[#0b264b] rounded-[20px] p-2 text-white text-center">
                    <span className="text-[9px] uppercase font-bold tracking-widest text-[#B31825]">{digitalTabData[digitalTab].phone1Title}</span>
                    <div className="space-y-1">
                      <span className="text-[8px] text-gray-400 block">Available Balance</span>
                      <span className="text-[11px] font-mono font-bold block text-white">{digitalTabData[digitalTab].phone1Balance}</span>
                    </div>
                    <div className="w-full h-1 bg-[#B31825] rounded-full mx-auto" />
                  </div>
                  <div className="w-[45px] h-[4px] bg-slate-800 rounded-full absolute bottom-1" />
                </div>

                {/* Smartphone Mockup 2 */}
                <div className="w-[125px] h-[240px] bg-slate-900 rounded-[28px] border-[5px] border-slate-800 shadow-2xl relative flex flex-col items-center justify-between p-2 overflow-hidden mx-2 shrink-0">
                  <div className="w-[60px] h-[10px] bg-slate-800 rounded-full absolute top-1" />
                  <div className="w-full flex-grow flex flex-col justify-between pt-4 bg-white rounded-[20px] p-2 text-navy text-center border border-gray-100">
                    <span className="text-[9px] uppercase font-bold tracking-widest text-[#B31825]">{digitalTabData[digitalTab].phone2Title}</span>
                    <div className="border border-dashed border-gray-200 rounded p-1 space-y-1 my-auto">
                      <div className="h-2 w-12 bg-slate-100 mx-auto rounded" />
                      <div className="h-1.5 w-16 bg-slate-50 mx-auto rounded" />
                      <div className="h-1.5 w-8 bg-slate-50 mx-auto rounded" />
                    </div>
                    <span className="text-[8px] text-[#00A86B] font-bold">🔒 Secured Clearing</span>
                  </div>
                  <div className="w-[45px] h-[4px] bg-slate-800 rounded-full absolute bottom-1" />
                </div>

                {/* Pointer Badge Right */}
                <div className="flex flex-col space-y-4 z-10">
                  {digitalTabData[digitalTab].indicatorsRight.map((ind, idx) => (
                    <div key={idx} className="bg-neutralBg border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm text-[11px] font-bold text-navy whitespace-nowrap animate-pulse">
                      {ind}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. DYNAMIC DEPOSITS & LOANS SELECTIONS */}
      <section className="py-24 bg-white text-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block">Premium Offerings</span>
              <h2 className="font-montserrat font-extrabold text-3xl text-navy mt-1">Our Financial Products</h2>
            </div>

            {/* Tab Toggles */}
            <div className="flex bg-neutralBg p-1.5 rounded-2xl mt-4 md:mt-0 border border-gray-200">
              <button
                onClick={() => setActiveTab('deposits')}
                className={`px-6 py-2.5 rounded-xl font-montserrat font-bold text-sm transition-all duration-300 ${activeTab === 'deposits' ? 'bg-primary text-white shadow-md scale-102' : 'text-gray-500 hover:text-navy'
                  }`}
              >
                Deposit Schemes
              </button>
              <button
                onClick={() => setActiveTab('loans')}
                className={`px-6 py-2.5 rounded-xl font-montserrat font-bold text-sm transition-all duration-300 ${activeTab === 'loans' ? 'bg-primary text-white shadow-md scale-102' : 'text-gray-500 hover:text-navy'
                  }`}
              >
                Loan Products
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories[activeTab].map((item, idx) => (
              <div
                key={idx}
                className="bg-neutralBg border border-gray-200/80 rounded-3xl p-6 hover:bg-white hover:border-[#B31825] hover:shadow-2xl transition-all duration-300 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-montserrat font-bold text-base text-navy leading-snug">{item.name}</h4>
                    <span className="bg-emerald/10 text-emerald text-xs font-bold px-2.5 py-1 rounded-full shrink-0">{item.rate}</span>
                  </div>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="pt-8">
                  <Link
                    to={activeTab === 'deposits' ? '/deposits' : '/loans'}
                    className="w-full text-center block py-3 bg-white border border-primary text-primary hover:bg-primary hover:text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                  >
                    Calculate & Apply
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WAYS TO BANK ACCORDION (Reference Layout input_file_4.png) */}
      <section className="py-24 bg-neutralBg text-navy border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left Column - Styled visual card representing Online Bank Setup */}
          <div className="lg:col-span-5 relative">
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-xl space-y-6 relative overflow-hidden flex flex-col justify-between min-h-[360px]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#B31825]/5 rounded-bl-full" />
              <div className="space-y-4">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest block">Ways to Bank</span>
                <h3 className="font-montserrat font-extrabold text-2xl text-navy">Secure access point on your terms</h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  Enjoy highly secure, real-time connectivity options. Read the guides on the right to start onboarding.
                </p>
              </div>

              {/* Decorative Card Illustration inside SVG */}
              <div className="w-full bg-[#052046]/5 rounded-2xl p-4 flex items-center space-x-4 border border-[#052046]/10">
                <div className="bg-[#B31825] text-white p-3 rounded-xl shadow-lg">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-xs text-navy uppercase tracking-wider">Multi-layered Encryption</h4>
                  <p className="text-[10px] text-gray-500 mt-0.5">Compliant with RBI digital co-operative banking mandates.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Accordion */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-widest block">Ways to Bank</span>
              <h2 className="font-montserrat font-extrabold text-3xl text-navy mt-1">Multiple ways of managing your finance with Sahyadri</h2>
            </div>

            <div className="space-y-3.5">
              {waysToBankData.map((way) => {
                const isExpanded = expandedWay === way.id;
                return (
                  <div
                    key={way.id}
                    className="border-b border-gray-200 pb-3 transition-all duration-200"
                  >
                    <button
                      onClick={() => setExpandedWay(isExpanded ? '' : way.id)}
                      className="w-full flex items-center justify-between text-left py-2 font-montserrat font-bold text-sm sm:text-base text-navy hover:text-[#B31825] focus:outline-none"
                    >
                      <span>{way.title}</span>
                      <span className="text-lg font-bold text-[#B31825] shrink-0 ml-4 font-mono select-none">
                        {isExpanded ? '—' : '+'}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500 leading-relaxed space-y-3 pl-1 font-light">
                        <p>{way.desc}</p>
                        <Link to={way.link} className="inline-flex items-center space-x-1 text-xs font-bold text-[#B31825] hover:underline">
                          <span>Know More</span>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 7. FINANCIAL CALCULATORS SHOWCASE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-2">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Interactive Tools</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Plan Your Investments & Loans</h2>
            <p className="text-gray-600 text-sm">Use our high-precision calculators to estimate FD compound growth or monthly EMI schedules.</p>
          </div>

          <Calculator />
        </div>
      </section>



      {/* 9. BLOGS & SPOTLIGHT SECTION (Reference Layout input_file_1.png) */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-100/40 text-navy relative border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column - Blogs (lg:col-span-8) */}
          <div className="lg:col-span-8 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <span className="text-[10px] font-bold text-primary tracking-widest uppercase block">MANAGE YOUR FINANCES BETTER</span>
                <h2 className="font-montserrat font-extrabold text-2xl text-navy">Blogs & Financial Literacy</h2>
              </div>
              <Link to="/news" className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider border-b border-[#B31825] pb-0.5 hover:opacity-85 select-none">
                VIEW ALL
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Blog Card 1 */}
              <div className="bg-white rounded-3xl p-5 border border-gray-200/50 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  {/* Rounded image */}
                  <div className="aspect-video w-full rounded-2xl bg-gradient-to-br from-[#052046]/10 to-[#B31825]/10 flex items-center justify-center overflow-hidden border border-gray-100">
                    <svg className="w-12 h-12 text-[#052046]/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                    </svg>
                  </div>
                  <h4 className="font-montserrat font-bold text-sm text-navy leading-snug">
                    How Digital Education Loan works for Rural Upliftment
                  </h4>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    A comprehensive guide for student members on tracking EMI payouts and securing subsidy schemes.
                  </p>
                </div>
                <div className="pt-6">
                  <Link to="/news" className="text-xs font-bold text-[#B31825] hover:underline">
                    Know More &gt;
                  </Link>
                </div>
              </div>

              {/* Blog Card 2 */}
              <div className="bg-white rounded-3xl p-5 border border-gray-200/50 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  {/* Rounded image */}
                  <div className="aspect-video w-full rounded-2xl bg-gradient-to-br from-[#B31825]/10 to-[#052046]/10 flex items-center justify-center overflow-hidden border border-gray-100">
                    <svg className="w-12 h-12 text-[#B31825]/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0110 21a3.745 3.745 0 01-3.068-1.593 3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0114 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                    </svg>
                  </div>
                  <h4 className="font-montserrat font-bold text-sm text-navy leading-snug">
                    Education Loan Checklist for Students & Parents
                  </h4>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Verify required certificates, address proofs, and evaluation criteria for instant rural education credit line.
                  </p>
                </div>
                <div className="pt-6">
                  <Link to="/news" className="text-xs font-bold text-[#B31825] hover:underline">
                    Know More &gt;
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Spotlight (lg:col-span-4) */}
          <div className="lg:col-span-4 space-y-6">
            <div className="border-b border-gray-200 pb-3">
              <span className="text-[10px] font-bold text-primary tracking-widest uppercase block">SPOTLIGHT</span>
              <h2 className="font-montserrat font-extrabold text-2xl text-navy">Know What's New</h2>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm max-h-[360px] overflow-y-auto space-y-4 no-scrollbar">
              {/* Notice 1 */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-semibold font-mono">2026-06-25</p>
                <div className="flex items-start justify-between gap-2">
                  <Link to="/news" className="text-xs sm:text-sm font-semibold text-navy hover:text-[#B31825] leading-snug block">
                    Digital Rupee by UBI | Offer Live! Get cashback on Digital Rupee transactions.
                  </Link>
                  <span className="bg-[#B31825] text-white text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0 animate-pulse">NEW</span>
                </div>
                <p className="text-[11px] text-gray-400">Valid till 14 Aug 2026. T&C apply. Union Bank of India Co-op Route.</p>
              </div>

              <hr className="border-gray-100" />

              {/* Notice 2 */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-semibold font-mono">2026-06-20</p>
                <div className="flex items-start justify-between gap-2">
                  <Link to="/news" className="text-xs sm:text-sm font-semibold text-navy hover:text-[#B31825] leading-snug block">
                    Financial Results for the Quarter/Year ended March 31, 2026.
                  </Link>
                  <span className="bg-[#B31825] text-white text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0">NEW</span>
                </div>
                <p className="text-[11px] text-gray-400">Audited statement sheets published for member transparency review.</p>
              </div>

              <hr className="border-gray-100" />

              {/* Notice 3 */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-semibold font-mono">2026-06-15</p>
                <div className="flex items-start justify-between gap-2">
                  <Link to="/news" className="text-xs sm:text-sm font-semibold text-navy hover:text-[#B31825] leading-snug block">
                    PSBs Hackathon Series 2026 Registration Open.
                  </Link>
                  <span className="bg-[#B31825] text-white text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0">NEW</span>
                </div>
                <p className="text-[11px] text-gray-400">Submit co-operative banking safety plugins to secure community funds.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. SUCCESS STORIES / TESTIMONIALS (Reference Layout input_file_0.png) */}
      <section
        className="py-24 relative overflow-hidden text-white border-b border-gray-200 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/testomonial bg.png')" }}
      >
        <h2 className="text-center font-montserrat font-extrabold italic text-3xl sm:text-4xl tracking-tight text-white mb-16 select-none">
          Testimonial
        </h2>

        {/* Image Background Container */}
        <div className="relative w-full min-h-[300px] flex items-center justify-center px-4">
          {/* Testimonial Cards Layer */}
          <div className="relative z-10 max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Card 1 */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-2xl flex flex-col justify-between space-y-6 hover:scale-[1.01] transition-transform duration-300 text-navy">
              <div className="space-y-4">
                <h4 className="font-montserrat font-extrabold text-sm sm:text-base text-navy uppercase tracking-wide text-center">
                  Sahyadri Co-Op's Satisfied Customers from Sangli
                </h4>
                <hr className="border-gray-100" />
                <p className="text-xs sm:text-sm italic text-gray-600 leading-relaxed text-center font-light">
                  "Sahyadri Co-Op Society has been a beacon of trust and reliability for our financial needs. Their personalized service and community-first approach make them truly stand out in cooperative banking."
                </p>
              </div>
              <div className="flex flex-col items-center space-y-2">
                {/* 5 orange stars */}
                <div className="flex space-x-1 text-orange-500 text-sm">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-2xl flex flex-col justify-between space-y-6 hover:scale-[1.01] transition-transform duration-300 text-navy">
              <div className="space-y-4">
                <h4 className="font-montserrat font-extrabold text-sm sm:text-base text-navy uppercase tracking-wide text-center">
                  A Valued Customer of Sahyadri Co-Op, CIDCO Sangli
                </h4>
                <hr className="border-gray-100" />
                <p className="text-xs sm:text-sm italic text-gray-600 leading-relaxed text-center font-light">
                  "They combine professionalism with a personal touch. Their commitment to empowering individuals and local communities is truly inspiring. The online member portal works flawlessly."
                </p>
              </div>
              <div className="flex flex-col items-center space-y-2">
                {/* 4 orange stars */}
                <div className="flex space-x-1 text-orange-500 text-sm">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span className="text-gray-200">★</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. CSR & COMMUNITY DEVELOPMENT TIMELINE */}
      <section className="py-24 bg-white text-navy border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-2">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">CSR & Outreach</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Driving Social Change</h2>
            <p className="text-gray-600 text-sm">We reinvest in community growth, helping rural households secure clean energy, literacy, and capital.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 bg-emerald/10 text-emerald rounded-2xl flex items-center justify-center mb-6">
                <Heart className="h-6 w-6" />
              </div>
              <h3 className="font-montserrat font-bold text-lg text-navy mb-3">Women Empowerment</h3>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Empowering small-scale home products and women-run SHGs with micro loans starting at interest rates below 8.0% p.a.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 bg-golden/10 text-golden-dark rounded-2xl flex items-center justify-center mb-6">
                <Briefcase className="h-6 w-6" />
              </div>
              <h3 className="font-montserrat font-bold text-lg text-navy mb-3">Financial Literacy camps</h3>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Hosting free monthly awareness workshops in rural blocks on secure digital transactions, ATM safety, and savings benefits.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mb-6">
                <Award className="h-6 w-6" />
              </div>
              <h3 className="font-montserrat font-bold text-lg text-navy mb-3">Farmer Seed Distribution</h3>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Direct distribution and credit subsidy for quality hybrid seeds, drip irrigation equipment, and soil diagnostics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 12. DYNAMIC PARTNER LOGOS */}
      <section className="bg-neutralBg py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">Our Integration & Banking Ecosystem Partners</p>
          <div className="flex flex-wrap items-center justify-center gap-12 opacity-65 select-none">
            <span className="font-montserrat font-extrabold text-gray-500 text-sm tracking-wider">RBI GUIDELINES</span>
            <span className="font-montserrat font-extrabold text-gray-500 text-sm tracking-wider">NPCI / IMPS</span>
            <span className="font-montserrat font-extrabold text-gray-500 text-sm tracking-wider">CRA COMPLIANT</span>
            <span className="font-montserrat font-extrabold text-gray-500 text-sm tracking-wider">CIBIL CHECK</span>
          </div>
        </div>
      </section>

      {/* 13. LARGE BANKING CTA BANNER */}
      <section className="py-24 bg-gradient-to-r from-primary to-primary-dark text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.05),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 relative z-10">
          <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-white">
            Looking for Secure Banking & Financial Growth?
          </h2>
          <p className="text-gray-200 text-sm sm:text-base max-w-2xl mx-auto font-light">
            Open your account today and experience trusted cooperative banking with modern digital services.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-4">
            <Link
              to="/register"
              className="w-full sm:w-auto px-8 py-4 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg text-center"
            >
              Open Account Now
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-4 border border-white/20 text-white rounded-xl hover:bg-white/10 transition-all font-bold text-xs uppercase tracking-wider text-center"
            >
              Talk to Branch Manager
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
